import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import {
  ArrowRight,
  TrendingUp,
  Clock,
  DollarSign,
  Users,
  RefreshCw,
  Building2,
  Star,
  UserPlus,
  MessageSquare,
} from "lucide-react";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { formatCurrency, formatDate } from "@/lib/demo-data";

const STAGES = [
  { key: "INSCRIPTION", label: "Inscription", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "ACTIVATION", label: "Activation", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { key: "ENGAGEMENT", label: "Engagement", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { key: "QUALIFIED", label: "Qualifie", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { key: "CONTACTED", label: "Contacte", color: "bg-orange-100 text-orange-700 border-orange-200" },
  { key: "CONVERTED", label: "Converti", color: "bg-green-100 text-green-700 border-green-200" },
] as const;

type StageKey = "INSCRIPTION" | "ACTIVATION" | "ENGAGEMENT" | "QUALIFIED" | "CONTACTED" | "CONVERTED";

export default function ConversionPipeline() {
  const navigate = useNavigate();
  const [stageFilter, setStageFilter] = useState<StageKey | "all">("all");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = trpc.admin.travauxbtp.getConversionPipeline.useQuery(
    {
      page,
      limit: 30,
      stage: stageFilter !== "all" ? stageFilter : undefined,
    } as any,
    { retry: false }
  );

  const stages = (data as any)?.stages;
  const kpis = (data as any)?.kpis;
  const items = (data as any)?.items ?? [];
  const totalPages = (data as any)?.totalPages ?? 1;

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Pipeline Conversion</h1>
            <p className="text-muted-foreground">
              Funnel artisan TravauxBTP vers SaaS
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Funnel visualization */}
        {stages && (
          <div className="grid grid-cols-6 gap-2">
            {STAGES.map((stage, idx) => {
              const stageData = stages[stage.key.toLowerCase() as keyof typeof stages] as any;
              const count = stageData?.count ?? 0;
              const isActive = stageFilter === stage.key;
              return (
                <button
                  key={stage.key}
                  onClick={() => {
                    setStageFilter(isActive ? "all" : stage.key);
                    setPage(1);
                  }}
                  className={`relative flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                    isActive
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-transparent hover:border-muted-foreground/20"
                  } ${stage.color}`}
                >
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs font-medium mt-1">{stage.label}</div>
                  {idx < STAGES.length - 1 && (
                    <ArrowRight className="absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* KPI cards */}
        {kpis && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{kpis.conversionRate}%</div>
                  <div className="text-xs text-muted-foreground">Taux de conversion</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{kpis.avgTimeToConvert}j</div>
                  <div className="text-xs text-muted-foreground">Temps moyen conversion</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{formatCurrency(kpis.pipelineValue / 100)}</div>
                  <div className="text-xs text-muted-foreground">Valeur pipeline</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{kpis.topProspects?.length ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Top prospects</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stage filter dropdown */}
        <div className="flex items-center gap-3">
          <Select
            value={stageFilter}
            onValueChange={(v) => {
              setStageFilter(v as StageKey | "all");
              setPage(1);
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrer par etape" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les etapes</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {(data as any)?.total ?? 0} artisan(s)
          </span>
        </div>

        {/* Company list table */}
        {isError ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <p className="text-sm text-muted-foreground mb-4">
                Erreur de chargement du pipeline
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reessayer
              </Button>
            </CardContent>
          </Card>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="h-10 w-10 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground">Aucun artisan dans cette etape</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Etape</TableHead>
                    <TableHead>Leads debloques</TableHead>
                    <TableHead>Depense totale</TableHead>
                    <TableHead>Score confiance</TableHead>
                    <TableHead>Inscrit le</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item: any) => {
                    const stageConfig = STAGES.find((s) => s.key === item.stage);
                    return (
                      <TableRow
                        key={item.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/companies/${item.id}`)}
                      >
                        <TableCell>
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.email}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${stageConfig?.color ?? ""} text-xs`}>
                            {stageConfig?.label ?? item.stage}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{item.matches}</TableCell>
                        <TableCell>{formatCurrency(item.totalSpent / 100)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-amber-500" />
                            {item.trustScore}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.createdAt ? formatDate(item.createdAt) : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            {item.stage !== "CONVERTED" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title="Creer lead Sales"
                                onClick={() =>
                                  navigate(
                                    `/sales/leads?newLead=1&company=${encodeURIComponent(item.name)}&email=${encodeURIComponent(item.email ?? "")}`
                                  )
                                }
                              >
                                <UserPlus className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="Contacter"
                              onClick={() =>
                                navigate(`/messages?company=${item.id}`)
                              }
                            >
                              <MessageSquare className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Precedent
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
